import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { collectTracking } from "@/lib/utm";
import LeadFields, { Field } from "@/components/LeadFields";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SITE_TYPES, CURRENT_CONCERNS, EXISTING_REPORTS } from "@/lib/constants";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export default function RiskScreen() {
  const [values, setValues] = useState({
    first_name: "", last_name: "", email: "", mobile: "", company: "",
    buyer_type: "", role: "", site_address: "", site_postcode_or_location: "",
    site_type: "", proposed_use: "", current_concern: "", existing_reports: "",
    decision_deadline: "", message: "", consent: false,
  });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.consent) return toast.error("Please tick the consent box to continue.");
    const required = ["site_address","site_postcode_or_location","site_type","proposed_use","current_concern","existing_reports"];
    for (const k of required) {
      if (!values[k]) return toast.error(`Please complete: ${k.replace(/_/g," ")}`);
    }
    setSubmitting(true);
    try {
      await api.post("/leads/risk-screen", {
        ...values,
        tracking: collectTracking({ formType: "risk_screen_request", enquiryType: "pfas_src_risk_screen_request" }),
      });
      setDone(true);
      toast.success("Risk Screen request received.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <SuccessBlock />;

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
      <div className="kicker mb-4">From £1,950 + VAT · 5–7 working days · Fixed fee</div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight">Request a PFAS Brownfield Risk Screen</h1>
      <p className="mt-6 text-white/65 max-w-2xl">
        A PFAS-specific desk-based intelligence review for a defined site. We assess potential PFAS source activities, historic land use, off-site influences, relevant pathways, available public evidence and key information gaps — and return a clear Red / Amber / Green decision view with recommended next steps.
      </p>
      <p className="mt-3 text-xs text-white/40 max-w-2xl">
        Complements, but does not replace, Phase 1 desk studies, contaminated-land consultants, laboratory testing or regulatory assessment.
      </p>

      <form onSubmit={submit} className="mt-10 border border-white/10 bg-[#0C0E13] p-6 md:p-10 space-y-10">
        <section>
          <div className="kicker mb-6">Your details</div>
          <LeadFields values={values} onChange={setField} showRole idPrefix="risk-screen"
            consentLabel="I agree to be contacted about my Risk Screen enquiry and receive relevant PFAS / brownfield risk information." />
        </section>

        <section className="border-t border-white/[0.06] pt-8 space-y-5">
          <div className="kicker mb-2">The site</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Site address" required>
              <Input value={values.site_address} onChange={(e)=>setField("site_address", e.target.value)}
                data-testid="risk-screen-site-address" className="bg-[#12141A] border-white/10 rounded-none h-11" required />
            </Field>
            <Field label="Site postcode or location" required>
              <Input value={values.site_postcode_or_location} onChange={(e)=>setField("site_postcode_or_location", e.target.value)}
                data-testid="risk-screen-site-postcode" className="bg-[#12141A] border-white/10 rounded-none h-11" required />
            </Field>
            <Field label="Site type" required>
              <Select value={values.site_type} onValueChange={(v)=>setField("site_type", v)}>
                <SelectTrigger data-testid="risk-screen-site-type" className="bg-[#12141A] border-white/10 rounded-none h-11">
                  <SelectValue placeholder="Select site type" />
                </SelectTrigger>
                <SelectContent className="bg-[#12141A] border-white/10 text-white">
                  {SITE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Proposed use" required>
              <Input value={values.proposed_use} onChange={(e)=>setField("proposed_use", e.target.value)}
                data-testid="risk-screen-proposed-use" className="bg-[#12141A] border-white/10 rounded-none h-11" required
                placeholder="e.g. Residential, mixed-use, logistics, public sector" />
            </Field>
          </div>
        </section>

        <section className="border-t border-white/[0.06] pt-8 space-y-5">
          <div className="kicker mb-2">The decision</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Current concern" required>
              <Select value={values.current_concern} onValueChange={(v)=>setField("current_concern", v)}>
                <SelectTrigger data-testid="risk-screen-current-concern" className="bg-[#12141A] border-white/10 rounded-none h-11">
                  <SelectValue placeholder="Select current concern" />
                </SelectTrigger>
                <SelectContent className="bg-[#12141A] border-white/10 text-white">
                  {CURRENT_CONCERNS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Do you have existing reports or site documents available?" required>
              <Select value={values.existing_reports} onValueChange={(v)=>setField("existing_reports", v)}>
                <SelectTrigger data-testid="risk-screen-existing-reports" className="bg-[#12141A] border-white/10 rounded-none h-11">
                  <SelectValue placeholder="Yes / No / Not sure" />
                </SelectTrigger>
                <SelectContent className="bg-[#12141A] border-white/10 text-white">
                  {EXISTING_REPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="When do you need a decision?" className="md:col-span-2">
              <Input value={values.decision_deadline} onChange={(e)=>setField("decision_deadline", e.target.value)}
                data-testid="risk-screen-decision-deadline" placeholder="e.g. Within 4 weeks, by end of Q2, pre-exchange"
                className="bg-[#12141A] border-white/10 rounded-none h-11" />
            </Field>
            <Field label="Anything else we should know?" className="md:col-span-2">
              <Textarea rows={5} value={values.message} onChange={(e)=>setField("message", e.target.value)}
                data-testid="risk-screen-message" placeholder="Planning reference, third-party questions, known concerns, transaction context…"
                className="bg-[#12141A] border-white/10 rounded-none" />
            </Field>
          </div>
        </section>

        <div className="pt-2 flex flex-wrap gap-4 items-center">
          <button type="submit" disabled={submitting}
            data-testid="risk-screen-submit-btn"
            className="inline-flex items-center gap-2 bg-white text-[#0A0B0E] px-6 py-3.5 text-[12px] font-mono-alt tracking-[0.18em] uppercase hover-lift hover:bg-white/90 disabled:opacity-40">
            {submitting ? "Sending…" : "Request Risk Screen"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>
          <span className="text-xs text-white/40 max-w-md">
            We will review your enquiry and confirm whether the Risk Screen is appropriate before invoicing.
          </span>
        </div>
      </form>
    </div>
  );
}

function SuccessBlock() {
  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-24" data-testid="risk-screen-success">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-center">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={2} />
        </div>
        <div className="kicker text-emerald-400">Received</div>
      </div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight">
        Thank you. We have received your Risk Screen request.
      </h1>
      <div className="mt-8 space-y-5 text-white/70 leading-relaxed max-w-3xl">
        <p>
          We will review your enquiry and contact you to understand the site, the decision you need to make, and whether the PFAS Brownfield Risk Screen is appropriate.
        </p>
        <p>
          If we proceed, we will ask you by email for any relevant documents, including site address, boundary plan, proposed use, planning reference, existing Phase 1 / Phase 2 reports and known contamination concerns.
        </p>
      </div>
      <div className="mt-10 border-t border-white/[0.08] pt-6 text-xs text-white/40">
        Your request has been logged securely. Response time: normally within one working day.
      </div>
    </div>
  );
}
